import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Avatar from '@mui/joy/Avatar';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import MenuButton from '@mui/joy/MenuButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import ListDivider from '@mui/joy/ListDivider';
import CircularProgress from '@mui/joy/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { ClientType, CLIENT_TYPE_LABELS } from '../../types/client';

export type WhatsappLinkStatus = 'connected' | 'connecting' | 'disconnected';

interface ChatHeaderProps {
  clientReference: string;
  clientName: string;
  phoneNumber: string;
  isSocketConnected: boolean;
  isWhatsappOperational: boolean;
  channel?: 'meta' | 'whatsapp_web';
  whatsappLinkStatus?: WhatsappLinkStatus | null;
  clientType?: ClientType;
  onBack: () => void;
  onUpdateClientType?: (
    clientReference: string,
    type: ClientType,
  ) => void | Promise<void>;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  clientReference,
  clientName,
  phoneNumber,
  isSocketConnected,
  isWhatsappOperational,
  channel = 'meta',
  whatsappLinkStatus = null,
  clientType,
  onBack,
  onUpdateClientType,
}) => {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const handleNavigateToClient = (): void => {
    navigate(`/clients/${clientReference}`);
  };
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleChangeType = async (newType: ClientType): Promise<void> => {
    if (!onUpdateClientType) return;
    if (clientType === newType) return;
    setIsUpdating(true);
    try {
      await onUpdateClientType(clientReference, newType);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 0,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <IconButton
          variant="plain"
          size="sm"
          onClick={onBack}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box
          onClick={handleNavigateToClient}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            cursor: 'pointer',
            borderRadius: 'sm',
            px: 0.5,
            '&:hover': { bgcolor: 'neutral.softHoverBg' },
          }}
        >
          <Avatar sx={{ bgcolor: 'primary.softBg', color: 'primary.softColor' }}>
            {getInitials(clientName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="title-sm" noWrap sx={{ '&:hover': { textDecoration: 'underline' } }}>
              {clientName}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              {phoneNumber}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Chip
          size="sm"
          variant="solid"
          color={channel === 'meta' ? 'primary' : 'warning'}
        >
          {channel === 'meta' ? 'Meta' : 'WhatsApp Web (Lectura)'}
        </Chip>
        {channel === 'whatsapp_web' && whatsappLinkStatus === 'connecting' ? (
          <Chip size="sm" variant="soft" color="warning">
            Conectando
          </Chip>
        ) : null}
        {channel === 'whatsapp_web' && whatsappLinkStatus === 'disconnected' ? (
          <Chip size="sm" variant="soft" color="danger">
            Desconectado
          </Chip>
        ) : null}
        {channel === 'whatsapp_web' && whatsappLinkStatus === 'connected' ? (
          <Chip size="sm" variant="soft" color="success">
            Conectado
          </Chip>
        ) : null}
        {channel === 'meta' ? (
          <Chip
            size="sm"
            variant="soft"
            color={isWhatsappOperational ? 'success' : 'danger'}
          >
            {isWhatsappOperational ? 'WhatsApp OK' : 'WhatsApp con inconvenientes'}
          </Chip>
        ) : null}
        <Chip size="sm" variant="soft" color={isSocketConnected ? 'success' : 'neutral'}>
          {isSocketConnected ? 'Tiempo real' : 'Sin tiempo real'}
        </Chip>
        <IconButton size="sm" variant="plain" color="neutral">
          <PhoneOutlinedIcon />
        </IconButton>
        <Dropdown>
          <MenuButton
            slots={{ root: IconButton }}
            slotProps={{ root: { size: 'sm', variant: 'plain', color: 'neutral' } }}
          >
            {isUpdating ? <CircularProgress size="sm" /> : <MoreVertIcon />}
          </MenuButton>
          <Menu placement="bottom-end" sx={{ minWidth: 240, p: 1 }}>
            <Typography
              level="body-xs"
              sx={{
                px: 1,
                py: 0.5,
                color: 'neutral.500',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Modificar tipo de cliente
            </Typography>
            <ListDivider />
            {(Object.values(ClientType) as ClientType[]).map((t) => {
              const selected = clientType === t;
              return (
                <MenuItem
                  key={t}
                  selected={selected}
                  disabled={!onUpdateClientType}
                  onClick={() => handleChangeType(t)}
                  sx={{ borderRadius: 'sm' }}
                >
                  <ListItemDecorator>
                    {selected ? (
                      <CheckIcon fontSize="small" color="primary" />
                    ) : (
                      <LabelOutlinedIcon fontSize="small" />
                    )}
                  </ListItemDecorator>
                  <ListItemContent>{CLIENT_TYPE_LABELS[t]}</ListItemContent>
                </MenuItem>
              );
            })}
          </Menu>
        </Dropdown>
      </Box>
    </Sheet>
  );
};

export default ChatHeader;
